import { useCallback, useEffect, useRef, useState } from 'react'
import { message } from 'antd'
import {
  AudioMutedOutlined,
  AudioOutlined,
  CameraOutlined,
  FormatPainterOutlined,
  PhoneFilled,
  SoundOutlined,
  VideoCameraAddOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons'
import { reportAnchorHangup } from '@/lib/anchorHangupActivity'
import { anchorApi } from '@/lib/anchorApi'
import { releaseThenLeaveForAnchorHangup } from '@/lib/anchorManualHangup'
import { getEnvConfig } from '@/lib/runtime-env'
import {
  shengwangClient,
  type BeautyRuntimeState,
  type CallMediaState,
} from '@/lib/shengwangClient'
import { useBeautySettingsStore } from '@/store/beautySettingsStore'
import { useAuthStore } from '@/store/authStore'
import { useCallStore } from '@/store/callStore'
import { usePresenceStore } from '@/store/presenceStore'
import type { BeautySettings } from '@/types/beauty'
import { CallSurfaceTopBar } from './CallSurfaceTopBar'

function formatElapsed(seconds: number) {
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`
}

function dataUrlToFile(dataUrl: string, fileName: string) {
  const [header, body] = dataUrl.split(',', 2)
  const mimeMatch = header.match(/data:([^;]+);base64/)
  const mimeType = mimeMatch?.[1] || 'image/jpeg'
  const binary = atob(body || '')
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  return new File([bytes], fileName, { type: mimeType })
}

export function ActiveCallView() {
  const beautyFeatureEnabled = getEnvConfig().ENABLE_BEAUTY
  const anchor = useAuthStore((state) => state.anchor)
  const currentCall = useCallStore((state) => state.currentCall)
  const endingCallOrderID = useCallStore((state) => state.endingCallOrderID)
  const setCurrentCall = useCallStore((state) => state.setCurrentCall)
  const setEndingCallOrderID = useCallStore(
    (state) => state.setEndingCallOrderID,
  )
  const setLastEndedCall = useCallStore((state) => state.setLastEndedCall)
  const setCallStatus = usePresenceStore((state) => state.setCallStatus)
  const localVideoRef = useRef<HTMLDivElement | null>(null)
  const remoteVideoRef = useRef<HTMLDivElement | null>(null)
  const beautyButtonRef = useRef<HTMLButtonElement | null>(null)
  const beautySheetRef = useRef<HTMLDivElement | null>(null)
  const mediaActionRef = useRef(false)
  const moderationUploadInFlightRef = useRef(false)
  const moderationFrameSeqRef = useRef(0)
  const callStartedAtRef = useRef<number | null>(null)
  const [mediaState, setMediaState] = useState<CallMediaState>(() =>
    shengwangClient.getMediaState(),
  )
  const [beautyState, setBeautyState] = useState<BeautyRuntimeState>(() =>
    shengwangClient.getBeautyState(),
  )
  const beautySettings = useBeautySettingsStore((state) => state.settings)
  const saveStatus = useBeautySettingsStore((state) => state.saveStatus)
  const initBeautySettings = useBeautySettingsStore((state) => state.init)
  const updateBeautySettings = useBeautySettingsStore(
    (state) => state.updateSettings,
  )
  const flushBeautySave = useBeautySettingsStore((state) => state.flushSave)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [ending, setEnding] = useState(false)
  const [beautySheetOpen, setBeautySheetOpen] = useState(false)
  const [isCompact, setIsCompact] = useState(false)
  const [pageVisible, setPageVisible] = useState(() =>
    typeof document === 'undefined'
      ? true
      : document.visibilityState !== 'hidden',
  )
  const [pendingMediaAction, setPendingMediaAction] = useState<
    'microphone' | 'speaker' | 'camera' | 'switch-camera' | null
  >(null)
  const currentCallKey = currentCall?.order_id || currentCall?.call_id
  const frameModerationEnabled = anchor?.frame_moderation_enabled === true
  const frameModerationIntervalMs =
    anchor?.frame_moderation_interval_seconds === 10 ? 10000 : 5000
  const endingRemoteCall = Boolean(
    currentCall?.order_id && endingCallOrderID === currentCall.order_id,
  )

  useEffect(
    () =>
      shengwangClient.subscribe(() =>
        setMediaState(shengwangClient.getMediaState()),
      ),
    [],
  )

  useEffect(
    () =>
      shengwangClient.subscribeBeautyState(() =>
        setBeautyState(shengwangClient.getBeautyState()),
      ),
    [],
  )

  useEffect(() => {
    if (!beautyFeatureEnabled) {
      return
    }
    initBeautySettings()
  }, [beautyFeatureEnabled, initBeautySettings])

  useEffect(() => {
    if (!beautyFeatureEnabled) {
      return
    }
    shengwangClient.setBeautySettings(beautySettings)
  }, [beautyFeatureEnabled, beautySettings])

  useEffect(() => {
    if (
      !beautyFeatureEnabled ||
      beautyState.capability !== 'overloaded' ||
      !beautySettings.enabled
    ) {
      return
    }
    updateBeautySettings({ enabled: false })
  }, [
    beautyFeatureEnabled,
    beautySettings.enabled,
    beautyState.capability,
    updateBeautySettings,
  ])

  useEffect(() => {
    if (!beautyFeatureEnabled) {
      return
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        void flushBeautySave()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [beautyFeatureEnabled, flushBeautySave])

  const closeBeautySheet = useCallback(() => {
    setBeautySheetOpen(false)
    void flushBeautySave()
  }, [flushBeautySave])

  const toggleBeautySheet = useCallback(() => {
    setBeautySheetOpen((open) => {
      if (open) {
        void flushBeautySave()
        return false
      }
      return true
    })
  }, [flushBeautySave])

  useEffect(() => {
    if (!beautySheetOpen) {
      return
    }
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Node)) {
        return
      }
      if (
        beautySheetRef.current?.contains(target) ||
        beautyButtonRef.current?.contains(target)
      ) {
        return
      }
      closeBeautySheet()
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [beautySheetOpen, closeBeautySheet])

  useEffect(() => {
    shengwangClient.setLocalVideoContainer(localVideoRef.current)
    shengwangClient.setRemoteVideoContainer(remoteVideoRef.current)
    return () => {
      shengwangClient.setLocalVideoContainer(null)
      shengwangClient.setRemoteVideoContainer(null)
    }
  }, [currentCallKey])

  useEffect(() => {
    callStartedAtRef.current = null
    moderationFrameSeqRef.current = 0
    moderationUploadInFlightRef.current = false
    setElapsedSeconds(0)
    setBeautySheetOpen(false)
    setIsCompact(false)
    if (beautyFeatureEnabled) {
      void flushBeautySave()
    }
  }, [currentCallKey])

  useEffect(() => {
    const updateVisibility = () => {
      setPageVisible(document.visibilityState !== 'hidden')
    }
    const handlePageHide = () => {
      setPageVisible(false)
    }
    document.addEventListener('visibilitychange', updateVisibility)
    window.addEventListener('pagehide', handlePageHide)
    return () => {
      document.removeEventListener('visibilitychange', updateVisibility)
      window.removeEventListener('pagehide', handlePageHide)
    }
  }, [])

  useEffect(() => {
    const orderID = currentCall?.order_id
    if (
      !orderID ||
      !frameModerationEnabled ||
      !mediaState.remoteUserJoined ||
      !mediaState.localVideoEnabled ||
      !pageVisible
    ) {
      return
    }
    moderationUploadInFlightRef.current = false
    let cancelled = false
    let activeController: AbortController | null = null

    const uploadFrame = async () => {
      if (cancelled || moderationUploadInFlightRef.current) {
        return
      }
      const frame = shengwangClient.captureLocalVideoFrame()
      if (!frame) {
        return
      }
      moderationUploadInFlightRef.current = true
      try {
        activeController = new AbortController()
        const frameSeq = moderationFrameSeqRef.current + 1
        const uploaded = await anchorApi.uploadModerationFrameImage(
          dataUrlToFile(frame.imageBase64, `video-frame-${orderID}-${frameSeq}.jpg`),
          { signal: activeController.signal },
        )
        if (cancelled || useCallStore.getState().currentCall?.order_id !== orderID) {
          return
        }
        const imageURL = uploaded.data?.storage_path || uploaded.data?.file_url || ''
        if (!imageURL) {
          throw new Error('uploaded moderation frame path is empty')
        }
        await anchorApi.uploadModerationFrame(orderID, {
          call_id: currentCall.call_id,
          frame_seq: frameSeq,
          captured_at: new Date().toISOString(),
          image_url: imageURL,
          width: frame.width,
          height: frame.height,
        }, { signal: activeController.signal })
        if (cancelled || useCallStore.getState().currentCall?.order_id !== orderID) {
          return
        }
        moderationFrameSeqRef.current = frameSeq
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return
        }
        // Moderation upload is best-effort and must not interrupt the active call.
      } finally {
        activeController = null
        moderationUploadInFlightRef.current = false
      }
    }
    const timer = window.setInterval(() => {
      void uploadFrame()
    }, frameModerationIntervalMs)
    return () => {
      cancelled = true
      activeController?.abort()
      window.clearInterval(timer)
    }
  }, [
    currentCall?.call_id,
    currentCall?.order_id,
    frameModerationEnabled,
    frameModerationIntervalMs,
    mediaState.localVideoEnabled,
    mediaState.remoteUserJoined,
    pageVisible,
  ])

  useEffect(() => {
    if (!currentCall) {
      setElapsedSeconds(0)
      return
    }
    if (!callStartedAtRef.current) {
      if (!mediaState.remoteUserJoined) {
        return
      }
      callStartedAtRef.current = Date.now()
    }
    const timer = window.setInterval(() => {
      const startedAt = callStartedAtRef.current
      setElapsedSeconds(
        startedAt ? Math.floor((Date.now() - startedAt) / 1000) : 0,
      )
    }, 1000)
    return () => window.clearInterval(timer)
  }, [currentCallKey, mediaState.remoteUserJoined])

  if (!currentCall) {
    return null
  }

  const hangup = async () => {
    if (ending || endingRemoteCall) {
      return
    }
    setEnding(true)
    if (currentCall.order_id) {
      setEndingCallOrderID(currentCall.order_id)
    }
    try {
      await releaseThenLeaveForAnchorHangup(currentCall)
      if (beautyFeatureEnabled) {
        await flushBeautySave()
      }
      reportAnchorHangup(currentCall, 'success')
      const startedAt = callStartedAtRef.current
      const durationSeconds = startedAt
        ? Math.floor((Date.now() - startedAt) / 1000)
        : elapsedSeconds
      setLastEndedCall({
        ...currentCall,
        status: 'ended',
        duration_seconds: durationSeconds,
      })
      setCurrentCall(null)
      setCallStatus('idle')
    } catch (error) {
      reportAnchorHangup(currentCall, 'failed', error)
      throw error
    } finally {
      if (
        currentCall.order_id &&
        useCallStore.getState().endingCallOrderID === currentCall.order_id
      ) {
        setEndingCallOrderID(null)
      }
      setEnding(false)
    }
  }

  const toggleMicrophone = async () => {
    await runMediaAction(
      'microphone',
      async () => {
        await shengwangClient.setLocalAudioEnabled(
          !shengwangClient.getMediaState().localAudioEnabled,
        )
      },
      'Failed to toggle microphone',
    )
  }

  const toggleCamera = async () => {
    await runMediaAction(
      'camera',
      async () => {
        await shengwangClient.setLocalVideoEnabled(
          !shengwangClient.getMediaState().localVideoEnabled,
        )
      },
      'Failed to toggle camera',
    )
  }

  const toggleRemoteAudio = async () => {
    await runMediaAction(
      'speaker',
      async () => {
        shengwangClient.setRemoteAudioEnabled(
          !shengwangClient.getMediaState().remoteAudioEnabled,
        )
      },
      'Failed to toggle speaker',
    )
  }

  const switchCamera = async () => {
    await runMediaAction(
      'switch-camera',
      async () => {
        await shengwangClient.switchCamera()
      },
      'This device does not support camera switching',
    )
  }

  const updateBeauty = (patch: Partial<BeautySettings>) => {
    updateBeautySettings(patch)
  }

  const runMediaAction = async (
    action: NonNullable<typeof pendingMediaAction>,
    operation: () => Promise<void>,
    errorMessage: string,
  ) => {
    if (mediaActionRef.current) {
      return
    }
    mediaActionRef.current = true
    setPendingMediaAction(action)
    try {
      await operation()
    } catch {
      message.error(errorMessage)
    } finally {
      mediaActionRef.current = false
      setPendingMediaAction(null)
    }
  }

  return (
    <div
      className={`active-call-overlay${isCompact ? ' active-call-overlay-compact' : ''}`}
    >
      <div
        className={`active-call-frame${isCompact ? ' active-call-frame-compact' : ''}`}
      >
        <div className="active-call-remote" ref={remoteVideoRef}>
          {!mediaState.remoteVideoAvailable ? (
            <div className="active-call-placeholder">
              <VideoCameraAddOutlined />
              <span>
                {mediaState.remoteUserJoined
                  ? 'Remote camera is off'
                  : 'Waiting for the other user'}
              </span>
            </div>
          ) : null}
        </div>

        <CallSurfaceTopBar
          className="active-call-top"
          compact={isCompact}
          center={
            <span className="active-call-timer">
              {formatElapsed(elapsedSeconds)}
            </span>
          }
          summaryUserID={currentCall.user_id || currentCall.from_user_id}
          onToggleCompact={() => setIsCompact((value) => !value)}
        />

        <div className="active-call-local">
          <div className="active-call-local-video" ref={localVideoRef}>
            {!mediaState.localVideoEnabled ? (
              <div className="active-call-local-placeholder">
                {(currentCall.nickname || 'Anchor').slice(0, 1)}
              </div>
            ) : null}
          </div>
        </div>

        <div className="active-call-controls">
          <button
            className="active-call-control"
            disabled={Boolean(pendingMediaAction)}
            type="button"
            onClick={() => void toggleMicrophone()}
          >
            {mediaState.localAudioEnabled ? (
              <AudioOutlined />
            ) : (
              <AudioMutedOutlined />
            )}
            <span>
              {mediaState.localAudioEnabled
                ? 'Microphone on'
                : 'Microphone off'}
            </span>
          </button>
          <button
            className="active-call-control"
            disabled={Boolean(pendingMediaAction)}
            type="button"
            onClick={() => void toggleRemoteAudio()}
          >
            <SoundOutlined />
            <span>
              {mediaState.remoteAudioEnabled ? 'Speaker on' : 'Speaker off'}
            </span>
          </button>
          <button
            className="active-call-control"
            disabled={Boolean(pendingMediaAction)}
            type="button"
            onClick={() => void toggleCamera()}
          >
            {mediaState.localVideoEnabled ? (
              <VideoCameraOutlined />
            ) : (
              <CameraOutlined />
            )}
            <span>
              {mediaState.localVideoEnabled ? 'Camera on' : 'Camera off'}
            </span>
          </button>
        </div>

        <button
          className="active-call-hangup"
          disabled={ending || endingRemoteCall}
          type="button"
          aria-label="Hang up"
          onClick={() => void hangup()}
        >
          <PhoneFilled />
        </button>

        <button
          className="active-call-switch-camera"
          disabled={
            Boolean(pendingMediaAction) ||
            mediaState.switchingCamera ||
            !mediaState.localVideoEnabled
          }
          type="button"
          aria-label="Switch camera"
          onClick={() => void switchCamera()}
        >
          <CameraOutlined />
        </button>

        {beautyFeatureEnabled ? (
          <>
            <button
              ref={beautyButtonRef}
              className="active-call-beauty-button"
              disabled={
                beautyState.capability === 'unsupported' ||
                beautyState.capability === 'overloaded'
              }
              type="button"
              aria-label="Beauty settings"
              onClick={toggleBeautySheet}
            >
              <FormatPainterOutlined />
            </button>

            {beautySheetOpen ? (
              <div
                ref={beautySheetRef}
                className="active-call-beauty-sheet"
                role="dialog"
                aria-label="Beauty settings"
              >
                <div className="active-call-beauty-sheet-header">
                  <span>Beauty</span>
                  <button type="button" onClick={closeBeautySheet}>
                    Done
                  </button>
                </div>
                <label className="active-call-beauty-toggle">
                  <input
                    checked={beautySettings.enabled}
                    type="checkbox"
                    onChange={(event) =>
                      updateBeauty({ enabled: event.target.checked })
                    }
                  />
                  <span>Enable beauty</span>
                </label>
                <BeautySlider
                  label="Brighten"
                  value={beautySettings.lightening_level}
                  onChange={(value) =>
                    updateBeauty({ lightening_level: value })
                  }
                />
                <BeautySlider
                  label="Smooth"
                  value={beautySettings.smoothness_level}
                  onChange={(value) =>
                    updateBeauty({ smoothness_level: value })
                  }
                />
                <BeautySlider
                  label="Rosy"
                  value={beautySettings.redness_level}
                  onChange={(value) => updateBeauty({ redness_level: value })}
                />
                <div className="active-call-beauty-footer">
                  <button
                    type="button"
                    onClick={() =>
                      updateBeauty({
                        enabled: false,
                        lightening_level: 0.35,
                        smoothness_level: 0.45,
                        redness_level: 0.2,
                      })
                    }
                  >
                    Reset
                  </button>
                  <span>{getBeautyStatusText(beautyState, saveStatus)}</span>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  )
}

function BeautySlider({
  label,
  value,
  onChange,
}: {
  label: string
  value: number
  onChange: (value: number) => void
}) {
  return (
    <label className="active-call-beauty-slider">
      <span>{label}</span>
      <input
        min="0"
        max="1"
        step="0.01"
        type="range"
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      <strong>{Math.round(value * 100)}</strong>
    </label>
  )
}

function getBeautyStatusText(
  beautyState: BeautyRuntimeState,
  saveStatus: string,
) {
  if (beautyState.capability === 'unsupported') {
    return 'Beauty is not supported on this device'
  }
  if (beautyState.capability === 'overloaded') {
    return 'Beauty was turned off due to device performance'
  }
  if (saveStatus === 'failed') {
    return 'Save failed · kept locally'
  }
  if (beautyState.applyStatus === 'applied') {
    return saveStatus === 'saved' ? 'Applied · Saved' : 'Applied'
  }
  if (saveStatus === 'saving') {
    return 'Saving'
  }
  if (saveStatus === 'dirty') {
    return 'Kept locally'
  }
  return 'Saved'
}
